import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Textarea } from "../ui/textarea";
import { Separator } from "../ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { toast } from "sonner";
import {
  Share2, Link2, Unlink, Settings, Send, Clock, CheckCircle,
  XCircle, Instagram, Facebook, Linkedin, Twitter, Image,
  Trash2, Eye, RefreshCw, Zap, PenLine, Globe, AlertCircle
} from "lucide-react";
import { API } from "../../apiConfig";


const PLATFORM_CONFIG = {
  facebook: {
    name: "Facebook",
    icon: Facebook,
    color: "bg-blue-600",
    textColor: "text-blue-600",
    fields: [
      { key: "page_access_token", label: "Page Access Token", type: "password" },
      { key: "page_id", label: "Page ID", type: "text" },
    ],
    helpUrl: "https://developers.facebook.com/docs/pages/getting-started",
  },
  instagram: {
    name: "Instagram",
    icon: Instagram,
    color: "bg-gradient-to-r from-purple-500 to-pink-500",
    textColor: "text-pink-600",
    fields: [
      { key: "access_token", label: "Access Token", type: "password" },
      { key: "instagram_account_id", label: "Instagram Business Account ID", type: "text" },
    ],
    helpUrl: "https://developers.facebook.com/docs/instagram-api/getting-started",
  },
  linkedin: {
    name: "LinkedIn",
    icon: Linkedin,
    color: "bg-blue-700",
    textColor: "text-blue-700",
    fields: [
      { key: "access_token", label: "Access Token", type: "password" },
      { key: "organization_urn", label: "Organization URN (urn:li:organization:XXXX)", type: "text" },
    ],
    helpUrl: "https://learn.microsoft.com/en-us/linkedin/marketing/",
  },
  twitter: {
    name: "X / Twitter",
    icon: Twitter,
    color: "bg-neutral-900",
    textColor: "text-neutral-900",
    fields: [
      { key: "api_key", label: "API Key (Consumer Key)", type: "password" },
      { key: "api_secret", label: "API Secret", type: "password" },
      { key: "access_token", label: "Access Token", type: "password" },
      { key: "access_token_secret", label: "Access Token Secret", type: "password" },
    ],
    helpUrl: "https://developer.twitter.com/en/docs/twitter-api",
  },
  pinterest: {
    name: "Pinterest",
    icon: Globe,
    color: "bg-red-600",
    textColor: "text-red-600",
    fields: [
      { key: "access_token", label: "Access Token", type: "password" },
      { key: "board_id", label: "Board ID", type: "text" },
    ],
    helpUrl: "https://developers.pinterest.com/docs/getting-started/",
  },
};

const SocialMediaTab = ({ token }) => {
  const [platforms, setPlatforms] = useState([]);
  const [posts, setPosts] = useState([]);
  const [autoPublishSettings, setAutoPublishSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [connectingPlatform, setConnectingPlatform] = useState(null);
  const [credentials, setCredentials] = useState({});
  const [accountName, setAccountName] = useState("");
  const [composerOpen, setComposerOpen] = useState(false);
  const [publishing, setPublishing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newPost, setNewPost] = useState({
    content: "",
    platforms: [],
    hashtags: "",
    images: [],
    imageUrl: "",
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [platformsRes, postsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/marketing/platforms`, { headers }),
        axios.get(`${API}/marketing/posts`, { headers }),
        axios.get(`${API}/marketing/auto-publish-settings`, { headers }),
      ]);
      setPlatforms(platformsRes.data);
      setPosts(postsRes.data);
      setAutoPublishSettings(settingsRes.data);
    } catch (err) {
      console.error("Failed to fetch social media data:", err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleConnect = (platformKey) => {
    setConnectingPlatform(platformKey);
    setCredentials({});
    setAccountName("");
    setConnectDialogOpen(true);
  };

  const saveConnection = async () => {
    if (!connectingPlatform) return;
    setSaving(true);
    try {
      await axios.post(
        `${API}/marketing/platforms/${connectingPlatform}/connect`,
        { credentials, account_name: accountName },
        { headers }
      );
      toast.success(`${PLATFORM_CONFIG[connectingPlatform].name} connected`);
      setConnectDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error("Failed to connect platform");
    } finally {
      setSaving(false);
    }
  };

  const disconnectPlatform = async (platformKey) => {
    try {
      await axios.delete(`${API}/marketing/platforms/${platformKey}`, { headers });
      toast.success(`${PLATFORM_CONFIG[platformKey].name} disconnected`);
      fetchData();
    } catch (err) {
      toast.error("Failed to disconnect");
    }
  };

  const toggleAutoPublish = async (platformKey, enabled) => {
    try {
      const updated = { ...autoPublishSettings, [platformKey]: enabled };
      await axios.put(
        `${API}/marketing/auto-publish-settings`,
        { settings: updated },
        { headers }
      );
      setAutoPublishSettings(updated);
      toast.success(
        enabled
          ? `Auto-publish enabled for ${PLATFORM_CONFIG[platformKey].name}`
          : `Auto-publish disabled for ${PLATFORM_CONFIG[platformKey].name}`
      );
    } catch (err) {
      toast.error("Failed to update setting");
    }
  };

  const togglePlatformInPost = (key) => {
    setNewPost((prev) => ({
      ...prev,
      platforms: prev.platforms.includes(key)
        ? prev.platforms.filter((p) => p !== key)
        : [...prev.platforms, key],
    }));
  };

  const addImageUrl = () => {
    if (newPost.imageUrl.trim()) {
      setNewPost((prev) => ({
        ...prev,
        images: [...prev.images, prev.imageUrl.trim()],
        imageUrl: "",
      }));
    }
  };

  const removeImage = (idx) => {
    setNewPost((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== idx),
    }));
  };

  const createAndPublish = async () => {
    if (!newPost.content.trim()) {
      toast.error("Post content is required");
      return;
    }
    if (newPost.platforms.length === 0) {
      toast.error("Select at least one platform");
      return;
    }
    setSaving(true);
    try {
      const hashtags = newPost.hashtags
        .split(",")
        .map((h) => h.trim())
        .filter(Boolean);
      const res = await axios.post(
        `${API}/marketing/posts`,
        {
          content: newPost.content,
          platforms: newPost.platforms,
          hashtags,
          images: newPost.images,
        },
        { headers }
      );
      const postId = res.data.post_id;
      // Now publish
      await axios.post(`${API}/marketing/posts/${postId}/publish`, {}, { headers });
      toast.success("Post published successfully");
      setComposerOpen(false);
      setNewPost({ content: "", platforms: [], hashtags: "", images: [], imageUrl: "" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to publish post");
    } finally {
      setSaving(false);
    }
  };

  const saveDraft = async () => {
    if (!newPost.content.trim()) {
      toast.error("Post content is required");
      return;
    }
    setSaving(true);
    try {
      const hashtags = newPost.hashtags
        .split(",")
        .map((h) => h.trim())
        .filter(Boolean);
      await axios.post(
        `${API}/marketing/posts`,
        {
          content: newPost.content,
          platforms: newPost.platforms,
          hashtags,
          images: newPost.images,
        },
        { headers }
      );
      toast.success("Draft saved");
      setComposerOpen(false);
      setNewPost({ content: "", platforms: [], hashtags: "", images: [], imageUrl: "" });
      fetchData();
    } catch (err) {
      toast.error("Failed to save draft");
    } finally {
      setSaving(false);
    }
  };

  const publishPost = async (postId) => {
    setPublishing(postId);
    try {
      await axios.post(`${API}/marketing/posts/${postId}/publish`, {}, { headers });
      toast.success("Published");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Publish failed");
    } finally {
      setPublishing(null);
    }
  };

  const deletePost = async (postId) => {
    try {
      await axios.delete(`${API}/marketing/posts/${postId}`, { headers });
      toast.success("Post deleted");
      fetchData();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const connectedPlatforms = platforms.filter((p) => p.is_connected);
  const disconnectedPlatforms = Object.keys(PLATFORM_CONFIG).filter(
    (key) => !platforms.find((p) => p.platform === key && p.is_connected)
  );

  const statusBadge = (status) => {
    const map = {
      published: { variant: "default", icon: CheckCircle, label: "Published" },
      draft: { variant: "secondary", icon: PenLine, label: "Draft" },
      scheduled: { variant: "outline", icon: Clock, label: "Scheduled" },
      failed: { variant: "destructive", icon: XCircle, label: "Failed" },
      partial: { variant: "outline", icon: AlertCircle, label: "Partial" },
      publishing: { variant: "outline", icon: RefreshCw, label: "Publishing..." },
    };
    const cfg = map[status] || map.draft;
    const Icon = cfg.icon;
    return (
      <Badge variant={cfg.variant} className="gap-1">
        <Icon className="h-3 w-3" />
        {cfg.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="social-media-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Share2 className="h-5 w-5" />
            Social Media
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Connect platforms, auto-publish new listings, and manage posts
          </p>
        </div>
        <Button
          onClick={() => setComposerOpen(true)}
          className="rounded-xl gap-2"
          data-testid="create-post-btn"
        >
          <PenLine className="h-4 w-4" />
          Create Post
        </Button>
      </div>

      {/* Connected Platforms */}
      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="text-base">Connected Platforms</CardTitle>
          <CardDescription>
            Manage your social media accounts and auto-publishing
          </CardDescription>
        </CardHeader>
        <CardContent>
          {connectedPlatforms.length === 0 && disconnectedPlatforms.length > 0 && (
            <p className="text-sm text-muted-foreground mb-4">
              No platforms connected yet. Connect one to start publishing.
            </p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Connected */}
            {connectedPlatforms.map((p) => {
              const cfg = PLATFORM_CONFIG[p.platform];
              if (!cfg) return null;
              const Icon = cfg.icon;
              return (
                <Card
                  key={p.platform}
                  className="rounded-xl border-2 border-green-200 bg-green-50/30"
                  data-testid={`platform-card-${p.platform}`}
                >
                  <CardContent className="pt-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`${cfg.color} p-2 rounded-lg text-white`}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{cfg.name}</p>
                          {p.account_name && (
                            <p className="text-xs text-muted-foreground">{p.account_name}</p>
                          )}
                        </div>
                      </div>
                      <Badge variant="default" className="bg-green-600 gap-1 text-xs">
                        <CheckCircle className="h-3 w-3" />
                        Connected
                      </Badge>
                    </div>

                    <Separator />

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Zap className="h-4 w-4 text-amber-500" />
                        <Label className="text-xs cursor-pointer" htmlFor={`auto-${p.platform}`}>
                          Auto-publish new listings
                        </Label>
                      </div>
                      <Switch
                        id={`auto-${p.platform}`}
                        checked={!!autoPublishSettings[p.platform]}
                        onCheckedChange={(val) => toggleAutoPublish(p.platform, val)}
                        data-testid={`auto-publish-toggle-${p.platform}`}
                      />
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 rounded-lg text-xs"
                        onClick={() => handleConnect(p.platform)}
                      >
                        <Settings className="h-3 w-3 mr-1" />
                        Update
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-600 rounded-lg text-xs"
                        onClick={() => disconnectPlatform(p.platform)}
                        data-testid={`disconnect-${p.platform}`}
                      >
                        <Unlink className="h-3 w-3 mr-1" />
                        Disconnect
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            {/* Available to Connect */}
            {disconnectedPlatforms.map((key) => {
              const cfg = PLATFORM_CONFIG[key];
              const Icon = cfg.icon;
              return (
                <Card
                  key={key}
                  className="rounded-xl border-dashed opacity-70 hover:opacity-100 transition-opacity"
                  data-testid={`platform-card-${key}`}
                >
                  <CardContent className="pt-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <div className={`${cfg.color} p-2 rounded-lg text-white`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{cfg.name}</p>
                        <p className="text-xs text-muted-foreground">Not connected</p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full rounded-lg text-xs"
                      onClick={() => handleConnect(key)}
                      data-testid={`connect-${key}`}
                    >
                      <Link2 className="h-3 w-3 mr-1" />
                      Connect {cfg.name}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Auto-Publish Info Banner */}
      <Card className="rounded-2xl border-amber-200 bg-amber-50/30">
        <CardContent className="pt-4 flex items-start gap-3">
          <Zap className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-sm">Auto-Publishing</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              When enabled, new product listings will automatically generate and publish a
              social media post to connected platforms. Posts include the product title,
              description excerpt, price, and primary image.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Post History */}
      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="text-base">Post History</CardTitle>
          <CardDescription>
            {posts.length} post{posts.length !== 1 ? "s" : ""} total
          </CardDescription>
        </CardHeader>
        <CardContent>
          {posts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Share2 className="h-8 w-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No posts yet. Create your first one or enable auto-publish.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {posts.map((post) => (
                <div
                  key={post.post_id}
                  className="flex items-start gap-4 p-4 bg-muted/30 rounded-xl"
                  data-testid={`post-item-${post.post_id}`}
                >
                  {/* Image preview */}
                  {post.images?.[0] && (
                    <img
                      src={post.images[0]}
                      alt=""
                      className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm line-clamp-2">{post.content}</p>
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      {statusBadge(post.status)}
                      {post.platforms?.map((p) => {
                        const cfg = PLATFORM_CONFIG[p];
                        return cfg ? (
                          <Badge key={p} variant="outline" className="text-xs gap-1">
                            {cfg.name}
                          </Badge>
                        ) : null;
                      })}
                      <span className="text-xs text-muted-foreground">
                        {post.created_at
                          ? new Date(post.created_at).toLocaleDateString()
                          : ""}
                      </span>
                    </div>
                    {/* Platform results */}
                    {post.platform_results && Object.keys(post.platform_results).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {Object.entries(post.platform_results).map(([plat, res]) => (
                          <Badge
                            key={plat}
                            variant={res.success ? "default" : "destructive"}
                            className="text-xs"
                          >
                            {plat}: {res.success ? "OK" : "Failed"}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    {post.status === "draft" && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-lg text-xs"
                        onClick={() => publishPost(post.post_id)}
                        disabled={publishing === post.post_id}
                        data-testid={`publish-post-${post.post_id}`}
                      >
                        {publishing === post.post_id ? (
                          <RefreshCw className="h-3 w-3 animate-spin" />
                        ) : (
                          <Send className="h-3 w-3 mr-1" />
                        )}
                        Publish
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="rounded-lg text-xs text-red-500"
                      onClick={() => deletePost(post.post_id)}
                      data-testid={`delete-post-${post.post_id}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Connect Dialog */}
      <Dialog open={connectDialogOpen} onOpenChange={setConnectDialogOpen}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle>
              {connectingPlatform && PLATFORM_CONFIG[connectingPlatform]
                ? `Connect ${PLATFORM_CONFIG[connectingPlatform].name}`
                : "Connect Platform"}
            </DialogTitle>
          </DialogHeader>
          {connectingPlatform && PLATFORM_CONFIG[connectingPlatform] && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="account-name">Account Name (optional)</Label>
                <Input
                  id="account-name"
                  value={accountName}
                  onChange={(e) => setAccountName(e.target.value)}
                  placeholder="e.g. @arawol_art"
                  className="rounded-lg mt-1"
                  data-testid="account-name-input"
                />
              </div>
              {PLATFORM_CONFIG[connectingPlatform].fields.map((field) => (
                <div key={field.key}>
                  <Label htmlFor={field.key}>{field.label}</Label>
                  <Input
                    id={field.key}
                    type={field.type}
                    value={credentials[field.key] || ""}
                    onChange={(e) =>
                      setCredentials((prev) => ({
                        ...prev,
                        [field.key]: e.target.value,
                      }))
                    }
                    className="rounded-lg mt-1"
                    data-testid={`credential-${field.key}`}
                  />
                </div>
              ))}
              <a
                href={PLATFORM_CONFIG[connectingPlatform].helpUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:underline flex items-center gap-1"
              >
                <Globe className="h-3 w-3" />
                How to get these credentials
              </a>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConnectDialogOpen(false)}
              className="rounded-lg"
            >
              Cancel
            </Button>
            <Button
              onClick={saveConnection}
              disabled={saving}
              className="rounded-lg"
              data-testid="save-connection-btn"
            >
              {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-1" /> : null}
              Save Connection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Post Composer Dialog */}
      <Dialog open={composerOpen} onOpenChange={setComposerOpen}>
        <DialogContent className="rounded-2xl max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Social Post</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Post Content</Label>
              <Textarea
                value={newPost.content}
                onChange={(e) => setNewPost((prev) => ({ ...prev, content: e.target.value }))}
                placeholder="Share something about your latest artwork..."
                rows={4}
                className="rounded-lg mt-1"
                data-testid="post-content-input"
              />
              <p className="text-xs text-muted-foreground mt-1 text-right">
                {newPost.content.length} / 280 chars (Twitter limit)
              </p>
            </div>

            <div>
              <Label>Platforms</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {connectedPlatforms.map((p) => {
                  const cfg = PLATFORM_CONFIG[p.platform];
                  if (!cfg) return null;
                  const isSelected = newPost.platforms.includes(p.platform);
                  return (
                    <Button
                      key={p.platform}
                      variant={isSelected ? "default" : "outline"}
                      size="sm"
                      className="rounded-lg text-xs gap-1"
                      onClick={() => togglePlatformInPost(p.platform)}
                      data-testid={`select-platform-${p.platform}`}
                    >
                      <cfg.icon className="h-3 w-3" />
                      {cfg.name}
                    </Button>
                  );
                })}
                {connectedPlatforms.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    No platforms connected. Connect one above first.
                  </p>
                )}
              </div>
            </div>

            <div>
              <Label>Hashtags (comma-separated)</Label>
              <Input
                value={newPost.hashtags}
                onChange={(e) => setNewPost((prev) => ({ ...prev, hashtags: e.target.value }))}
                placeholder="AboriginalArt, Arawol, IndigenousArt"
                className="rounded-lg mt-1"
                data-testid="hashtags-input"
              />
            </div>

            <div>
              <Label>Images</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={newPost.imageUrl}
                  onChange={(e) =>
                    setNewPost((prev) => ({ ...prev, imageUrl: e.target.value }))
                  }
                  placeholder="Paste image URL"
                  className="rounded-lg flex-1"
                  data-testid="image-url-input"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addImageUrl}
                  className="rounded-lg"
                >
                  <Image className="h-4 w-4" />
                </Button>
              </div>
              {newPost.images.length > 0 && (
                <div className="flex gap-2 mt-2 flex-wrap">
                  {newPost.images.map((url, i) => (
                    <div key={i} className="relative">
                      <img
                        src={url}
                        alt=""
                        className="w-16 h-16 rounded-lg object-cover"
                      />
                      <button
                        onClick={() => removeImage(i)}
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5"
                      >
                        <XCircle className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={saveDraft}
              disabled={saving}
              className="rounded-lg"
              data-testid="save-draft-btn"
            >
              Save Draft
            </Button>
            <Button
              onClick={createAndPublish}
              disabled={saving}
              className="rounded-lg gap-1"
              data-testid="publish-now-btn"
            >
              {saving ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              Publish Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SocialMediaTab;
